package co.maytes.standupbot.dashboard.pages.app.analytics

import co.maytes.standupbot.dashboard.styles.Layout
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import co.maytes.standupbot.dashboard.components.feedback.EmptyState
import co.maytes.standupbot.dashboard.components.feedback.ErrorState
import co.maytes.standupbot.dashboard.components.feedback.SkeletonChart
import co.maytes.standupbot.dashboard.components.feedback.SkeletonStatRow
import co.maytes.standupbot.dashboard.components.layout.AppShell
import co.maytes.standupbot.dashboard.components.layout.AuthGuard
import co.maytes.standupbot.dashboard.components.motion.Revalidating
import co.maytes.standupbot.dashboard.components.motion.StateSwap
import co.maytes.standupbot.dashboard.components.widgets.AppPageHeader
import co.maytes.standupbot.dashboard.components.widgets.Button
import co.maytes.standupbot.dashboard.components.widgets.ButtonVariant
import co.maytes.standupbot.dashboard.components.widgets.StatCard
import co.maytes.standupbot.dashboard.components.widgets.icons.LucideBarChart
import co.maytes.standupbot.dashboard.components.widgets.icons.LucideCreditCard
import co.maytes.standupbot.dashboard.components.widgets.icons.LucideMessagesSquare
import co.maytes.standupbot.dashboard.components.widgets.icons.LucideTerminal
import co.maytes.standupbot.dashboard.components.widgets.icons.LucideUsers
import co.maytes.standupbot.dashboard.features.analytics.AnalyticsCsvExport
import co.maytes.standupbot.dashboard.features.analytics.AnalyticsPhase
import co.maytes.standupbot.dashboard.features.analytics.CostChart
import co.maytes.standupbot.dashboard.features.analytics.DateRange
import co.maytes.standupbot.dashboard.features.analytics.DateRangePicker
import co.maytes.standupbot.dashboard.features.analytics.ParticipationChart
import co.maytes.standupbot.dashboard.features.analytics.ResourceState
import co.maytes.standupbot.dashboard.features.analytics.WeeklyActivityChart
import co.maytes.standupbot.dashboard.features.analytics.isPending
import co.maytes.standupbot.dashboard.features.analytics.rememberAnalyticsStore
import co.maytes.standupbot.dashboard.stores.rememberAuthStore
import co.maytes.standupbot.dashboard.styles.Palette
import co.maytes.standupbot.dashboard.styles.Spacing
import co.maytes.standupbot.dashboard.styles.Typography
import co.maytes.standupbot.shared.dto.analytics.CostsDto
import co.maytes.standupbot.shared.dto.analytics.ParticipationDto
import co.maytes.standupbot.shared.dto.analytics.VelocityDto
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.maxWidth
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.px

@Page("/app/analytics")
@Composable
fun AnalyticsPage() {
    AuthGuard {
        AppShell {
            AnalyticsContent()
        }
    }
}

@Composable
private fun AnalyticsContent() {
    val auth = rememberAuthStore()
    val store = rememberAnalyticsStore()
    val workspaceId = auth.currentUser?.workspace?.id

    var range by remember { mutableStateOf(DateRange.default()) }

    LaunchedEffect(workspaceId, range.cacheKey) {
        if (!workspaceId.isNullOrBlank()) store.loadAll(workspaceId, range)
    }

    Box(
        modifier = Modifier
            .fillMaxWidth()
            .styleModifier { property("margin", "0 auto") }
            .maxWidth(Layout.wide),
    ) {
        Column(modifier = Modifier.fillMaxWidth().gap(Spacing.lg)) {
            HeroRow(
                range = range,
                onRangeChange = { range = it },
                onExportCsv = {
                    if (store.isRefreshing) return@HeroRow
                    val costs = (store.costsState as? ResourceState.Loaded<CostsDto>)?.value
                    val velocity = (store.velocityState as? ResourceState.Loaded<VelocityDto>)?.value
                    val participation = (store.participationState as? ResourceState.Loaded<ParticipationDto>)?.value
                    AnalyticsCsvExport.export(
                        costs = costs,
                        velocity = velocity,
                        participation = participation,
                        rangeLabel = range.label,
                    )
                },
            )

            Revalidating(refreshing = store.isRefreshing) {
                Column(modifier = Modifier.fillMaxWidth().gap(Spacing.lg)) {
                    val costsLoaded = store.costsState as? ResourceState.Loaded<CostsDto>
                    val velocityLoaded = store.velocityState as? ResourceState.Loaded<VelocityDto>
                    val participationLoaded = store.participationState as? ResourceState.Loaded<ParticipationDto>

                    if (costsLoaded == null && participationLoaded == null &&
                        (store.costsState.isPending() || store.participationState.isPending())
                    ) {
                        SkeletonStatRow(5)
                    } else {
                        StatCardRow(
                            costs = costsLoaded?.value,
                            participation = participationLoaded?.value,
                        )
                    }

                    val allLoaded = costsLoaded != null && velocityLoaded != null && participationLoaded != null
                    val anyLoading = store.costsState.isPending() ||
                        store.velocityState.isPending() ||
                        store.participationState.isPending()
                    val hasAnyData = costsLoaded != null && velocityLoaded != null && participationLoaded != null && (
                        costsLoaded.value.months.any { it.totalCents > 0L || it.standupCount > 0 } ||
                            velocityLoaded.value.weeks.any { it.standupsPosted > 0 || it.sectionsGenerated > 0 } ||
                            participationLoaded.value.users.isNotEmpty()
                    )

                    val phase = AnalyticsPhase.of(
                        costs = store.costsState,
                        velocity = store.velocityState,
                        participation = store.participationState,
                        hasAnyData = hasAnyData,
                    )
                    StateSwap(state = phase) {
                        Column(modifier = Modifier.fillMaxWidth().gap(Spacing.lg)) {
                            if (allLoaded && !hasAnyData) {
                                val (title, subtitle) = if (range.totalDays < 90) {
                                    "Quiet here" to "No activity in the last ${range.totalDays} day${if (range.totalDays == 1) "" else "s"}. Try expanding the range to 30 or 90 days."
                                } else {
                                    "No activity yet" to "Your charts fill in once your first standup posts."
                                }
                                EmptyState(title = title, subtitle = subtitle, icon = { LucideBarChart(size = 26) })
                            } else if (anyLoading) {
                                Box(modifier = Modifier.fillMaxWidth()) {
                                    LoadingBlock()
                                }
                            } else {
                                Box(modifier = Modifier.fillMaxWidth()) {
                                    when (val s = store.costsState) {
                                        ResourceState.Idle, ResourceState.Loading -> LoadingBlock()
                                        is ResourceState.Error -> ErrorState(message = s.error.displayMessage)
                                        is ResourceState.Loaded -> CostChart(dto = s.value)
                                    }
                                }

                                Box(modifier = Modifier.fillMaxWidth()) {
                                    when (val s = store.velocityState) {
                                        ResourceState.Idle, ResourceState.Loading -> LoadingBlock()
                                        is ResourceState.Error -> ErrorState(message = s.error.displayMessage)
                                        is ResourceState.Loaded -> WeeklyActivityChart(dto = s.value)
                                    }
                                }

                                Box(modifier = Modifier.fillMaxWidth()) {
                                    when (val s = store.participationState) {
                                        ResourceState.Idle, ResourceState.Loading -> LoadingBlock()
                                        is ResourceState.Error -> ErrorState(message = s.error.displayMessage)
                                        is ResourceState.Loaded -> ParticipationChart(dto = s.value)
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun HeroRow(
    range: DateRange,
    onRangeChange: (DateRange) -> Unit,
    onExportCsv: () -> Unit,
) {
    Column(modifier = Modifier.fillMaxWidth().gap(Spacing.md)) {
        AppPageHeader(
            eyebrow = "analytics",
            title = "Analytics",
            subtitle = "Track AI spend, standup participation, and weekly activity. Data refreshes every 5 minutes.",
        )

        Row(
            verticalAlignment = Alignment.CenterVertically,
            modifier = Modifier
                .fillMaxWidth()
                .gap(Spacing.md)
                .styleModifier { property("flex-wrap", "wrap") },
        ) {
            DateRangePicker(value = range, onChange = onRangeChange)
            Box(
                modifier = Modifier.styleModifier { property("margin-left", "auto") },
            ) {
                Button(
                    label = "Export CSV",
                    variant = ButtonVariant.Ghost,
                    onClick = onExportCsv,
                )
            }
        }
    }
}

@Composable
private fun StatCardRow(costs: CostsDto?, participation: ParticipationDto?) {
    val totalCents = costs?.months?.sumOf { it.totalCents } ?: 0L
    val standupCount = costs?.months?.sumOf { it.standupCount } ?: 0
    val totalInputTokens = costs?.months?.sumOf { it.inputTokens } ?: 0L
    val totalOutputTokens = costs?.months?.sumOf { it.outputTokens } ?: 0L
    val totalTokens = totalInputTokens + totalOutputTokens
    val contributors = participation?.users?.filter { !it.optedOut }.orEmpty()
    val avgParticipation = if (contributors.isNotEmpty()) {
        contributors.sumOf { it.participationPct } / contributors.size
    } else 0

    Row(
        modifier = Modifier
            .fillMaxWidth()
            .gap(Spacing.md)
            .styleModifier {
                property("flex-wrap", "wrap")
                property("align-items", "stretch")
            },
    ) {
        Box(modifier = Modifier.styleModifier {
            property("flex", "1 1 11rem")
            property("display", "flex")
        }) {
            StatCard(
                label = "Total spend",
                icon = { LucideCreditCard(size = 16) },
                value = formatDollars(totalCents),
                subtitle = "across selected range",
            )
        }
        Box(modifier = Modifier.styleModifier {
            property("flex", "1 1 11rem")
            property("display", "flex")
        }) {
            StatCard(
                label = "Standups posted",
                icon = { LucideMessagesSquare(size = 16) },
                value = standupCount.toString(),
                subtitle = if (standupCount == 1) "single run" else "synthesized",
            )
        }
        Box(modifier = Modifier.styleModifier {
            property("flex", "1 1 11rem")
            property("display", "flex")
        }) {
            StatCard(
                label = "Participation",
                icon = { LucideUsers(size = 16) },
                value = "$avgParticipation%",
                subtitle = "avg across ${contributors.size} " +
                    if (contributors.size == 1) "contributor" else "contributors",
            )
        }
        Box(modifier = Modifier.styleModifier {
            property("flex", "1 1 11rem")
            property("display", "flex")
        }) {
            StatCard(
                label = "Claude tokens",
                icon = { LucideTerminal(size = 16) },
                value = formatTokens(totalTokens),
                subtitle = "${formatTokens(totalInputTokens)} in · ${formatTokens(totalOutputTokens)} out",
            )
        }
        Box(modifier = Modifier.styleModifier {
            property("flex", "1 1 11rem")
            property("display", "flex")
        }) {
            val avgPerStandup = if (standupCount > 0) totalTokens / standupCount else 0L
            StatCard(
                label = "Tokens / standup",
                icon = { LucideBarChart(size = 16) },
                value = formatTokens(avgPerStandup),
                subtitle = "avg across $standupCount run${if (standupCount == 1) "" else "s"}",
            )
        }
    }
}

private fun formatTokens(n: Long): String = when {
    n >= 1_000_000 -> "${formatScaled(n.toDouble() / 1_000_000.0)}M"
    n >= 1_000 -> "${formatScaled(n.toDouble() / 1_000.0)}K"
    else -> n.toString()
}

private fun formatScaled(value: Double): String {
    val rounded = kotlin.math.round(value * 10.0) / 10.0
    val asInt = rounded.toLong()
    return if (rounded == asInt.toDouble()) asInt.toString() else rounded.toString()
}

private fun formatDollars(cents: Long): String {
    val whole = cents / 100
    val fraction = (cents % 100).toString().padStart(2, '0')
    return "\$${whole}.${fraction}"
}

@Composable
private fun LoadingBlock() {
    Column(modifier = Modifier.fillMaxWidth().gap(Spacing.md)) {
        repeat(3) { SkeletonChart() }
    }
}
